'use client';

import { createTheme } from '@mui/material/styles';

// Las tres familias del sistema. Nunca redefinirlas localmente: importar desde aquí.
export const SERIF_FONT =
  "var(--font-cormorant), 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
export const SANS_FONT =
  "var(--font-mulish), Mulish, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
export const MONO_FONT =
  "var(--font-ibm-plex-mono), 'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace";

// ── Paleta ────────────────────────────────────────────────────
// primary = dorado (acentos y CTAs) · navy = fondos oscuros · cream = fondos claros.
// Los mismos valores viven como CSS vars en globals.scss (para SCSS modules) y
// como tokens de Tailwind en tailwind.css. Este archivo es la fuente.
const theme = createTheme({
  palette: {
    primary: {
      main: '#c2a15c', // dorado de marca
      light: '#d8bd82', // hover del dorado
      lighter: '#e8cf94', // texto dorado sobre navy
      dark: '#b08d4a', // dorado sobre fondos claros (contraste AA)
      contrastText: '#14213c',
    },
    navy: {
      main: '#14213c', // fondo oscuro base
      light: '#1a2947',
      lighter: '#203355',
      dark: '#0f1a30', // sección de contacto
      darkest: '#0b1425', // footer
      line: '#2a3a5a', // bordes de inputs sobre navy
      header: 'rgba(18,28,52,0.86)',
    },
    cream: {
      main: '#f7f3ec', // fondo claro base
      dark: '#efe8db', // fondo claro alterno (áreas)
      line: '#e8e0d1', // borde de tarjetas sobre crema
      lineStrong: '#d8cdb8', // separadores del grid de áreas
      lineSoft: '#ece4d5', // divisores internos de tarjeta
    },
    text: {
      primary: '#1b2a44', // títulos sobre claro
      secondary: '#4a5164', // cuerpo sobre claro
      disabled: '#6a7080',
      heading: '#f6f1e8', // títulos sobre navy
      onDark: '#c3cbdb', // cuerpo sobre navy
      onDarkMuted: '#96a1b8', // secundario sobre navy
      onDarkFaint: '#6f7a90', // notas al pie sobre navy
    },
    background: {
      default: '#ffffff',
      paper: '#f7f3ec',
    },
    grey: {
      900: '#1b2a44',
      800: '#2a3a5a',
      700: '#4a5164',
      600: '#5a6072',
      500: '#6a7080',
      400: '#8892a6',
      300: '#aeb8cc',
      200: '#d8cdb8',
      100: '#e8e0d1',
      50: '#f7f3ec',
    },
    error: { main: '#b3261e' },
  },

  typography: {
    fontFamily: SANS_FONT,
    fontFamilySerif: SERIF_FONT,
    fontFamilyMono: MONO_FONT,
    fontSize: 16,
    h1: {
      fontFamily: SERIF_FONT,
      fontSize: 'clamp(42px, 6vw, 78px)',
      fontWeight: 500,
      lineHeight: 1.04,
      letterSpacing: '0.005em',
      // Reparte las líneas de forma pareja: evita que la última quede con una
      // sola palabra huérfana (los títulos de hero rondan las 3 líneas).
      textWrap: 'balance',
    },
    h2: {
      fontFamily: SERIF_FONT,
      fontSize: 'clamp(30px, 4vw, 50px)',
      fontWeight: 500,
      lineHeight: 1.1,
    },
    h3: {
      fontFamily: SERIF_FONT,
      fontSize: 'clamp(26px, 3vw, 36px)',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: SERIF_FONT,
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: SERIF_FONT,
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '16px',
      lineHeight: 1.8,
    },
    body2: {
      fontSize: '14.5px',
      lineHeight: 1.75,
    },
    caption: {
      fontFamily: MONO_FONT,
      fontSize: '11px',
      letterSpacing: '0.14em',
      lineHeight: 1.4,
    },
  },

  spacing: 8,
  // El diseño es de ángulos rectos: sin radios en botones, tarjetas ni inputs.
  shape: { borderRadius: 0 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        html: { fontSize: '16px', scrollBehavior: 'smooth' },
        body: {
          fontFamily: SANS_FONT,
          color: '#1b2a44',
          background: '#14213c',
          overflowX: 'hidden',
        },
        '::selection': { background: '#c2a15c', color: '#14213c' },
        'img, video': { display: 'block', maxWidth: '100%' },
        a: { color: 'inherit', textDecoration: 'none' },
        button: { font: 'inherit', cursor: 'pointer' },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontFamily: SANS_FONT,
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          borderRadius: 0,
          padding: '16px 34px',
          lineHeight: 1.2,
          boxShadow: 'none',
          transition:
            'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease',
          '&:hover': { boxShadow: 'none' },
          ...(ownerState.size === 'small' && {
            padding: '9px 20px',
            fontSize: '12px',
          }),
          ...(ownerState.variant === 'contained' && {
            backgroundColor: '#c2a15c',
            color: '#14213c',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#d8bd82' },
          }),
          ...(ownerState.variant === 'outlined' && {
            borderColor: '#c2a15c',
            borderWidth: '1px',
            borderStyle: 'solid',
            color: '#e8cf94',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#c2a15c',
              borderColor: '#c2a15c',
              color: '#14213c',
            },
          }),
          ...(ownerState.variant === 'text' && {
            padding: 0,
            color: '#b08d4a',
            letterSpacing: '0.1em',
            '&:hover': { backgroundColor: 'transparent', color: '#c2a15c' },
          }),
        }),
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: '#c2a15c',
          textDecoration: 'none',
          transition: 'color 0.25s ease',
          '&:hover': { color: '#d8bd82' },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#e8e0d1' },
      },
    },
  },
});

export default theme;
